import { z } from "zod";
import { EntityProfileType, FirmSize } from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import { WorkspaceTimezone } from "@common/workspaceTimezone";
import { FilterMultiselectOption } from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { servicesOfferedLabels } from "@components/entity/constants";
import { format, startOfYear, subYears } from "date-fns";
import { COUNTRIES } from "@common/countries";

export const organizationSchema = z.object({
    organizationName: z.string().min(1, "Firm name is required"),
    firmSize: z.nativeEnum(FirmSize, { required_error: "Select a size" }),
    servicesOffered: z
        .array(z.nativeEnum(OrganizationServicesOffered))
        .min(1, "At least one practice is required"),
});

const organizationClientSchema = z.object({
    businessName: z.string().min(1, "Name is required"),
    legalType: z.nativeEnum(EntityProfileType, {
        required_error: "Legal type is required",
    }),
    bookkeepingStartDate: z
        .string()
        .min(1, "Bookkeeping start date is required"),
    timezone: z.nativeEnum(WorkspaceTimezone, {
        required_error: "Timezone is required",
    }),
});

export const createOrganizationFormSchema = organizationSchema.merge(
    organizationClientSchema,
);

export type CreateOrganizationFormSchema = z.infer<
    typeof createOrganizationFormSchema
>;

export const servicesOfferedOptions: FilterMultiselectOption[] = Object.values(
    OrganizationServicesOffered,
).map((value) => ({
    value,
    label: servicesOfferedLabels[value],
}));

export const clientCountriesOptions: FilterMultiselectOption[] = COUNTRIES.map(
    (country) => ({
        value: country.code,
        label: country.name,
        searchableLabel: country.name,
    }),
);

export function bookkeepingStartDateAsValue(date: Date) {
    return format(date, "yyyy-MM-dd");
}

export const defaultDemoBookkeepingStartDate = subYears(
    startOfYear(new Date()),
    1,
);
